import { z } from 'zod'

/**
 * Zod validator for a Firestore Timestamp field.
 *
 * Lenient-on-read by design: during the timestamp standardization migration,
 * the same field can be read as a Firestore Timestamp (native SDK read),
 * `{seconds, nanoseconds}` (JSON wire shape), JS Date (pre-migration write),
 * Unix ms number (pre-migration Group C fields), or ISO string (legacy
 * `deletedAt`, `promoCodes.expirationDate`). The validator accepts any of
 * these so reads never throw while migrations are in flight.
 *
 * Write-site enforcement (Timestamp-only) lives at the write call sites, not
 * here — the converter's lenient-on-read / strict-on-write split already
 * covers that.
 */
export const firestoreTimestamp = z.custom<
  { toMillis(): number } | { seconds: number; nanoseconds: number } | Date | number | string
>(
  (value) => {
    if (value === null || value === undefined) return false
    if (typeof value === 'number') return Number.isFinite(value)
    if (value instanceof Date) return !Number.isNaN(value.getTime())
    if (typeof value === 'string') return !Number.isNaN(Date.parse(value))
    if (typeof value === 'object') {
      if ('toMillis' in value && typeof (value as { toMillis: unknown }).toMillis === 'function') {
        return true
      }
      if (
        'seconds' in value &&
        'nanoseconds' in value &&
        typeof (value as { seconds: unknown }).seconds === 'number' &&
        typeof (value as { nanoseconds: unknown }).nanoseconds === 'number'
      ) {
        return true
      }
    }
    return false
  },
  { message: 'Expected a Firestore Timestamp, Date, Unix ms number, or ISO string' }
)
